import { useId, useRef, type InputHTMLAttributes, type ReactNode } from "react";

type NativeInput = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "size" | "className">;

interface PickerFieldProps extends NativeInput {
  readonly label: string;
  readonly type: "date" | "time";
  readonly value: string;
  /** What the box says before anything is chosen — the invitation to tap. */
  readonly prompt: string;
  /** How the chosen value reads, e.g. "1 May 1990" rather than "1990-05-01". */
  readonly display?: string;
  readonly icon: ReactNode;
}

/**
 * A native date/time picker drawn as an obvious, full-width tap target.
 *
 * iOS Safari renders an empty date input as nothing at all — no placeholder,
 * no border — so the first question of first run had no visible place to tap.
 * The native input still does the picking (the phone's own wheel), but it
 * sits invisibly over a bordered box that says what to do.
 */
export function PickerField({ label, type, value, prompt, display, icon, ...rest }: PickerFieldProps) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  const chosen = value !== "";

  function open() {
    // Chrome and recent Safari can open the picker programmatically; where
    // they can't, the tap lands on the input itself, which opens it anyway.
    try {
      ref.current?.showPicker?.();
    } catch {
      ref.current?.focus();
    }
  }

  return (
    <div className="w-full">
      <label htmlFor={id} className="label mb-2 block">
        {label}
      </label>
      <div
        onClick={open}
        className={`relative flex min-h-16 w-full cursor-pointer items-center justify-between gap-3 border-[1.5px] px-5 transition-colors focus-within:border-ink ${
          chosen ? "border-ink" : "border-mid"
        }`}
      >
        <span className={`font-display text-[22px] leading-tight md:text-[24px] ${chosen ? "text-ink" : "text-mid"}`}>
          {chosen ? (display ?? value) : prompt}
        </span>
        <span aria-hidden className="text-ink">
          {icon}
        </span>
        <input
          ref={ref}
          id={id}
          type={type}
          value={value}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          {...rest}
        />
      </div>
    </div>
  );
}

export function CalendarIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3.5" y="5" width="17" height="15.5" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
    </svg>
  );
}

export function ClockIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}
